/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import axios from 'axios';
import { HTTPError /* , ConnectionError */ } from 'data/utils/errors';
import * as apiConstants from 'data/constants/api';
import * as sessionConstants from 'data/constants/session';
import * as sessionSelectors from 'data/selectors/session';

// eslint-disable-next-line
import { jwtDecode } from 'jwt-decode';

// const wait = time => new Promise(res => setTimeout(res, time));

class API {
  constructor(store, ...axiosArgs) {
    this.refreshInApi = this.refreshInApi.bind(this);
    this.errorInterceptor = this.errorInterceptor.bind(this);
    this.successInterceptor = this.successInterceptor.bind(this);
    this.setAuthorization = this.setAuthorization.bind(this);
    this.removeAuthorization = this.removeAuthorization.bind(this);
    this.requestInterceptor = this.requestInterceptor.bind(this);

    this.axios = axios.create(...axiosArgs);
    this.store = store;
    this.axios.interceptors.response.use(this.successInterceptor, this.errorInterceptor);
    this.axios.interceptors.request.use(this.requestInterceptor, null);
  }

  static noRefresh = 'No token';

  static isRefreshUrl(url) {
    return new RegExp(/\/api\/token\/refresh/).test(url);
  }

  static isAuthUrl(url) {
    return new RegExp(/\/api\/token/).test(url);
  }

  async refreshInApi() {
    const refresh = sessionSelectors.getRefresh(this.store.getState());
    if (!refresh) throw new Error(API.noRefresh);
    const res = await this.axios.post('/api/token/refresh/', { refresh });
    this.setAuthorization(res.data.access);
    this.store.dispatch({
      type: sessionConstants.REFRESH_SUCCESS,
      payload: {
        ...res.data,
      },
    });

    return res.data;
  }

  successInterceptor(response) {
    this.store.dispatch({ type: apiConstants.SUCCESS });
    return response;
  }

  requestInterceptor(request) {
    const oldAccess = sessionSelectors.getAccess(this.store.getState());

    if (oldAccess && request.url !== '/api/token/refresh/') {
      const { exp } = jwtDecode(oldAccess);
      if (Date.now() >= exp * 1000) {
        return this.refreshInApi().then(({ access }) => {
          request.headers.Authorization = `Bearer ${access}`;
          return request;
        });
      }
    }

    return request;
  }

  errorInterceptor(error) {
    if (error.response) {
      switch (error.response.status) {
        case 400:
          if (error.config) {
            const reqConfig = error.config;
            if (reqConfig.url === `${reqConfig.baseURL}/api/token/refresh/`) {
              this.store.dispatch({
                type: sessionConstants.LOGOUT,
              });
            }
          }
          break;
        case 401:
          if (error.config) {
            const reqConfig = error.config;
            if (reqConfig.url === `${reqConfig.baseURL}/api/token/refresh/`) {
              this.store.dispatch({
                type: sessionConstants.LOGOUT,
              });
            }
            if (!API.isAuthUrl(reqConfig.url)) {
              return this.refreshInApi().then(({ access }) => {
                reqConfig.headers.Authorization = `Bearer ${access}`;
                return this.axios.request(reqConfig);
              });
            }

            // .catch(e => {
            //   if (e.message === API.noRefresh) {
            //     this.store.dispatch({
            //       type: sessionConstants.LOGOUT,
            //     });
            //   }
            // })
          }

          this.store.dispatch({ type: apiConstants.UNAUTHORIZED });
          // throw new HTTPError('Unauthorized', error.response.status);
          break;
        case 500:
          this.store.dispatch({ type: apiConstants.SERVER_ERROR });
          throw new HTTPError('Server Error', error.response.status);
        case 404:
          if (error.config?.skipGlobalErrorHandling) {
            return Promise.reject(error);
          }
          // For specific endpoints we want to handle 404s locally (e.g. form-level validation),
          // so we skip triggering the global NOT_FOUND handler.
          if (error.config) {
            const url = error.config.url || '';
            // WeChat union ID user creation endpoint: handle 404 in the action instead of showing global 404 page
            const isWechatUserCreation404 = /(\/)?api\/v1\/sites\/\d+\/users\/wechat\/?$/i.test(url);
            if (isWechatUserCreation404) {
              return Promise.reject(error);
            }
          }

          this.store.dispatch({ type: apiConstants.NOT_FOUND });
          throw new HTTPError('Not Found', error.response.status);
        case 403:
          this.store.dispatch({ type: apiConstants.FORBIDDEN });
          throw new HTTPError('Forbidden', error.response.status);
        case 429:
          this.store.dispatch({ type: apiConstants.TOO_MANY_REQUESTS });
          throw new HTTPError('Too Many Requests', error.response.status);
        default:
          break;
      }
    } else if (!navigator.onLine) {
      this.store.dispatch({ type: apiConstants.CONNECTION_ERROR });
      // throw new ConnectionError('No connection');
      // if (error.config) {
      //   const reqConfig = error.config;
      //
      //   console.log('Connection error, will retry in 3 sec');
      //   return wait(3000).then(() => this.axios.request(reqConfig));
      // }
    }
    return Promise.reject(error);
  }

  setAuthorization(apiKey) {
    if (apiKey) {
      this.axios.defaults.headers.common.Authorization = `Bearer ${apiKey}`;
    }
  }

  setLanguage(language) {
    this.axios.defaults.headers['Accept-Language'] = language;
  }

  removeAuthorization() {
    delete this.axios.defaults.headers.common.Authorization;
  }

  get = (...args) => this.axios.get.apply(this, args);
  post = (...args) => this.axios.post.apply(this, args);
  patch = (...args) => this.axios.patch.apply(this, args);
  put = (...args) => this.axios.put.apply(this, args);
  delete = (...args) => this.axios.delete.apply(this, args);
}

export default API;
